// SPDX-FileCopyrightText: 2026 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { useEffect } from 'react';

type GlobalKeyboardEventType = 'keydown' | 'keyup';

const ACTIVE_MODAL_SELECTOR = '[role="dialog"][aria-modal="true"], [role="alertdialog"][aria-modal="true"]';
const EDITABLE_ELEMENT_SELECTOR =
  'input, textarea, select, [contenteditable]:not([contenteditable="false"]), ' +
  '[role="textbox"], [role="searchbox"], [role="combobox"], [role="spinbutton"]';

export function shouldSuppressGlobalKeyboardEvent(event: KeyboardEvent) {
  if (event.defaultPrevented) return true;

  if (document.querySelector(ACTIVE_MODAL_SELECTOR) !== null) return true;

  return event.composedPath().some(target => target instanceof Element && target.matches(EDITABLE_ELEMENT_SELECTOR));
}

export default function useGlobalKeyboardEvent(
  type: GlobalKeyboardEventType,
  listener: (event: KeyboardEvent) => void,
  enabled = true
) {
  useEffect(() => {
    if (!enabled) return;

    const guardedListener = (event: KeyboardEvent) => {
      if (shouldSuppressGlobalKeyboardEvent(event)) return;
      listener(event);
    };

    window.addEventListener(type, guardedListener);

    return () => {
      window.removeEventListener(type, guardedListener);
    };
  }, [enabled, listener, type]);
}
